import { useCallback, useEffect, useMemo, useRef } from "react";
import type { SWRInfiniteResponse } from "swr/infinite";
import { APIEndpointEnum } from "../../../enums/endpoint";
import {
  getRealtimeAccount,
  getRealtimeSnapshotItems,
  getRealtimeSnapshotSignature,
  normalizeRealtimeChannel,
  parseRealtimeMessage,
} from "../../../utils/realtimeRevalidation";
import { forEachFrame } from "../../wss/parseFrame";
import { PositionWithIdentity } from "../../../utils/positionIdentity";
import { debounceRevalidation } from "../../../utils/revalidation";
import {
  subscribeTradeExecuted,
  TradeExecutedDetail,
} from "../../../utils/tradeEvents";
import {
  canBind,
  clearSyncStatus,
  ConvergenceRun,
  getPendingCloses,
  openPendingClose,
  orderListSignature,
  positionListSignature,
  releasePendingClose,
  releasePendingClosesFor,
  RETRY_POLICY,
  runUntilConverged,
  setLive,
  setStale,
  setSyncing,
} from "../../../services/tradeSync";
import {
  applyRealtimeOpenOrderEvent,
  applyRealtimeOpenOrdersSnapshot,
} from "./realtimeOpenOrders";
import {
  getPositionEventAction,
  isClosedPositionAction,
  revalidateFirstPageOnly,
} from "./useCursorPaginatedHistory.helpers";
// Shared with usePositionsWSS on purpose: both hooks write to the one
// pending-close registry, so they must agree on what counts as a close.
import { isClosedPositionPatch } from "../../wss/usePositionsWSS.helpers";
import { NormalizedPage } from "./useCursorPaginatedHistory.types";

interface UseCursorPaginatedHistoryRealtimeParams<T> {
  swrMutate: SWRInfiniteResponse<NormalizedPage<T>>["mutate"];
  /**
   * The pages currently on screen. This is the BASELINE a convergence run
   * compares the server against, so it must come from the live cache — a run
   * that starts from an empty baseline treats the very first answer as proof
   * the server caught up, which proves nothing at all.
   */
  pages?: NormalizedPage<T>[];
  enabled: boolean;
  /**
   * Whether the owner is actually FETCHING — enabled AND credentials present.
   * The convergence target must arm on this rather than on enabled alone: armed
   * without credentials, every refetch resolves undefined against a null SWR
   * key, the signature never changes, and the run spends its whole budget only
   * to publish a STALE that proves nothing.
   */
  isReady: boolean;
  authenticated: boolean;
  realtimeChannels?: string[];
  triggerSubscribe: (
    op: string,
    data: string[],
    autoUnsubToChannelString?: string,
  ) => void;
  /** Releases what `triggerSubscribe` acquired (M1). */
  triggerUnsubscribe: (data: string[]) => void;
  lastMessages: MessageEvent<any>[] | null;
  accountKey?: string;
  endpoint: APIEndpointEnum;
  queryString: string;
  isPositionsEndpoint: boolean;
  isOpenOrdersEndpoint: boolean;
}

export function useCursorPaginatedHistoryRealtime<T>({
  swrMutate,
  pages,
  enabled,
  isReady,
  authenticated,
  realtimeChannels,
  triggerSubscribe,
  triggerUnsubscribe,
  lastMessages,
  accountKey,
  endpoint,
  queryString,
  isPositionsEndpoint,
  isOpenOrdersEndpoint,
}: UseCursorPaginatedHistoryRealtimeParams<T>): void {
  // Latest pages seen, so a convergence run can compare the server's new answer
  // against what was on screen when the trade confirmed.
  const lastPagesRef = useRef<NormalizedPage<T>[] | undefined>(pages);
  // Track what is actually on screen, every render. Seeding this only from a
  // run own refetch left the FIRST trade of a session comparing against an
  // empty baseline: any non-empty answer then read as changed, so the run
  // stopped after one request and reported LIVE without the server ever having
  // shown the trade.
  lastPagesRef.current = pages;
  const runRef = useRef<ConvergenceRun | undefined>(undefined);
  const targetId = `${endpoint}:${accountKey || "anon"}:${queryString}`;
  const processedRealtimeEvents = useRef<Set<string>>(new Set());
  const lastRealtimeSnapshots = useRef<Map<string, string>>(new Map());

  const realtimeSnapshotQuery = useMemo(() => {
    if (!queryString) return undefined;
    const params: Record<string, string> = {};
    new URLSearchParams(queryString).forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [queryString]);

  // ── Realtime subscriptions ──────────────────────────────────────────────
  // Keyed by content: a caller passing a new array with the same channels must
  // not release and re-acquire them (an unsubscribe + subscribe on the wire).
  const realtimeChannelsKey = realtimeChannels?.join("|") ?? "";
  useEffect(() => {
    if (!enabled || !authenticated || !realtimeChannelsKey) return undefined;
    const channels = realtimeChannelsKey.split("|");
    channels.forEach((channel) => {
      triggerSubscribe("subscribe", [channel]);
    });
    // Release on unmount, account change, disable or de-auth (M1).
    return () => triggerUnsubscribe(channels);
  }, [authenticated, enabled, realtimeChannelsKey, triggerSubscribe, triggerUnsubscribe]);

  // Register the close in the shared registry instead of deleting the row from
  // the SWR cache.
  //
  // The old version wrote `swrMutate(..., { revalidate: false })` to strip the
  // row, which destroyed the server's answer: once deleted there was nothing to
  // fall back to, so a close we could not uniquely identify silently removed a
  // position the trader still held. It also matched on ANY of eight candidate
  // key spellings, four of which omit margin type — so closing an isolated leg
  // hid the cross leg on the same market and side.
  //
  // The registry is shared with `usePositionsWSS`, so the bottom-bar table and
  // the account-derived map used by the trade form, chart marks and the
  // Positions page can no longer disagree about whether a position exists.
  const registerPendingClose = useCallback(
    (positionPatch: PositionWithIdentity, proof: "receipt" | "broadcast") => {
      if (!accountKey) return false;
      openPendingClose({ account: accountKey, position: positionPatch, proof });
      return true;
    },
    [accountKey],
  );

  // A live (non-close) patch is the server telling us the position is still
  // there — positive evidence that supersedes a pending close for it.
  const releaseOnLivePatch = useCallback(
    (positionPatch: PositionWithIdentity) => {
      if (!accountKey) return;
      getPendingCloses(accountKey).forEach((entry) => {
        if (canBind(entry.fingerprint, positionPatch)) {
          releasePendingClose(entry.id);
        }
      });
    },
    [accountKey],
  );

  const applyOpenOrdersSnapshotToCache = useCallback(
    (snapshotOrders: T[]) => {
      if (!isOpenOrdersEndpoint) return;

      void swrMutate(
        (pages: NormalizedPage<T>[] | undefined) =>
          applyRealtimeOpenOrdersSnapshot(
            pages,
            snapshotOrders,
            realtimeSnapshotQuery,
          ),
        { revalidate: false },
      );
    },
    [isOpenOrdersEndpoint, realtimeSnapshotQuery, swrMutate],
  );

  const applyOpenOrderEventToCache = useCallback(
    (rawOrder: Record<string, unknown> | undefined, action?: unknown) => {
      if (!isOpenOrdersEndpoint) return;

      void swrMutate(
        (pages: NormalizedPage<T>[] | undefined) =>
          applyRealtimeOpenOrderEvent(
            pages,
            rawOrder,
            action,
            realtimeSnapshotQuery,
          ),
        { revalidate: false },
      );
    },
    [isOpenOrdersEndpoint, realtimeSnapshotQuery, swrMutate],
  );

  // One trailing-edge refetch of the FIRST page per burst of events, shared by
  // every mounted table on the same key (FE-9). This used to refetch every
  // loaded page three times (250/1250/3000 ms) per event per table. A realtime
  // event changes the head of the list; later pages keep their cursors until
  // the trader pages again (the post-trade convergence run below still
  // refetches everything).
  const revalidateOnRealtime = useCallback(() => {
    // Owned by this table's cache (L3): the key leaves out the page size, so two
    // tables on the same query must not replace each other's pending run.
    debounceRevalidation(
      `history:${targetId}`,
      () => {
        void swrMutate((current) => current, {
          revalidate: revalidateFirstPageOnly,
        });
      },
      { owner: swrMutate },
    );
  }, [swrMutate, targetId]);

  // ── Local trade catch-up ────────────────────────────────────────────────
  // `tx.wait(1)` only proves the chain accepted the trade; the backend indexer
  // still needs a moment before `/positions` returns the new row. Refetch
  // immediately and then on a widening ladder so the position shows up as soon
  // as it is queryable, instead of waiting on a websocket push that may lag.
  //
  // This is the only path that can refresh these caches from the order flow:
  // they are `useSWRInfinite` caches, and SWR's key-filter `mutate` skips
  // `$inf$` keys outright (see `src/utils/tradeEvents.ts`).
  const revalidateAfterLocalTrade = useCallback((detail: TradeExecutedDetail) => {
    // A confirmed full close is already decided — drop the row now. This also
    // tombstones its identity, so a `/positions` response still in flight (or
    // fetched by the ladder below before the indexer catches up) cannot
    // resurrect it: the registry projects it out of every consumer's rows.
    if (isPositionsEndpoint && detail.openedPosition && accountKey) {
      // Our own confirmed open supersedes a pending close on the same market
      // and side — see `releasePendingClosesFor`.
      releasePendingClosesFor(accountKey, detail.openedPosition);
    }
    if (isPositionsEndpoint && detail.closedPosition) {
      registerPendingClose(detail.closedPosition, detail.closeProof || "broadcast");
    }

    // Refetch until the server's answer actually changes, then stop.
    //
    // This replaces a fixed six-rung ladder that fired every rung whether the
    // data arrived on the first read or never — seven requests per trade per
    // list, unconditionally. The signature below is the evidence: a new
    // position, a resized one, a filled or cancelled order all change it. When
    // the backend is quick this now costs a single request; when it never
    // catches up the run is bounded and the target is marked stale rather than
    // presenting an unconfirmed list as current.
    const signatureOf = (pages?: NormalizedPage<T>[]) => {
      const rows = (pages || []).flatMap((page) => page.data);
      return isPositionsEndpoint
        ? positionListSignature(rows as PositionWithIdentity[])
        : orderListSignature(rows as unknown[]);
    };

    const before = signatureOf(lastPagesRef.current);
    runRef.current?.cancel();
    setSyncing(targetId, 0, RETRY_POLICY.maxAttempts);

    const run = runUntilConverged<NormalizedPage<T>[] | undefined>({
      refetch: async () => {
        const next = (await swrMutate()) as NormalizedPage<T>[] | undefined;
        lastPagesRef.current = next;
        return next;
      },
      isConverged: (next) => signatureOf(next) !== before,
      onAttempt: (attempt) => setSyncing(targetId, attempt, RETRY_POLICY.maxAttempts),
    });
    runRef.current = run;

    void run.done.then((outcome) => {
      if (runRef.current !== run) return;
      if (outcome.status === "cancelled") {
        // A cancelled run proved nothing — it was superseded by a newer trade,
        // or the component went away. Claiming the data is confirmed current
        // would be asserting something we never checked.
        return;
      }
      if (outcome.status === "converged") {
        setLive(targetId);
      } else if (outcome.status === "errored") {
        setStale(targetId, "NETWORK");
      } else {
        setStale(targetId, "NOT_CONVERGED");
      }
    });
  }, [accountKey, isPositionsEndpoint, registerPendingClose, swrMutate, targetId]);

  useEffect(() => {
    if (!isReady || !accountKey) return undefined;
    if (!isPositionsEndpoint && !isOpenOrdersEndpoint) return undefined;
    return subscribeTradeExecuted(revalidateAfterLocalTrade);
  }, [
    accountKey,
    isReady,
    isOpenOrdersEndpoint,
    isPositionsEndpoint,
    revalidateAfterLocalTrade,
  ]);

  useEffect(
    () => () => {
      runRef.current?.cancel();
      clearSyncStatus(targetId);
    },
    [targetId],
  );

  useEffect(() => {
    if (
      !enabled ||
      !lastMessages?.length ||
      !realtimeChannels?.length ||
      !accountKey
    ) {
      return;
    }
    const channelSet = new Set(
      realtimeChannels.map((c) => c.split(":")[0].toLowerCase()),
    );
    let shouldRevalidate = false;

    forEachFrame(lastMessages, "useCursorPaginatedHistoryRealtime", (msg) => {
      const parsed = parseRealtimeMessage(msg.data);
      const channel = normalizeRealtimeChannel(parsed?.channel);
      const isAccountEvent =
        channel &&
        channelSet.has(channel) &&
        getRealtimeAccount(parsed) === accountKey &&
        parsed?.data !== undefined;
      if (!isAccountEvent) return;

      const snapshotSignature = getRealtimeSnapshotSignature(parsed, channel);
      if (snapshotSignature !== undefined) {
        const snapshotKey = `${channel}:${accountKey}`;
        const previousSignature = lastRealtimeSnapshots.current.get(snapshotKey);
        lastRealtimeSnapshots.current.set(snapshotKey, snapshotSignature);
        if (previousSignature === snapshotSignature) {
          return;
        }

        const snapshotItems = getRealtimeSnapshotItems(parsed, channel);
        if (
          isOpenOrdersEndpoint &&
          channel === "orders" &&
          Array.isArray(snapshotItems)
        ) {
          applyOpenOrdersSnapshotToCache(snapshotItems as T[]);
        }
      }

      const eventId = `${channel}:${accountKey}:${JSON.stringify(
        parsed?.data || null,
      )}`;
      if (processedRealtimeEvents.current.has(eventId)) return;
      processedRealtimeEvents.current.add(eventId);
      if (processedRealtimeEvents.current.size > 200) {
        const oldest = processedRealtimeEvents.current.values().next().value;
        if (oldest) processedRealtimeEvents.current.delete(oldest);
      }

      if (isPositionsEndpoint && channel === "positions") {
        const action = getPositionEventAction(parsed?.data);
        const positionPatch = parsed?.data?.position as
          | PositionWithIdentity
          | undefined;

        // A close arrives EITHER as an explicit close/liquidate `action` OR as a
        // terminal-status / zero-size patch carrying no action at all — which is
        // the shape liquidations and raw indexer close events actually use.
        //
        // Both conditions must be checked here, because `usePositionsWSS` checks
        // both and the two hooks write to the SAME registry. Checking only the
        // action meant a zero-size close patch fell through to the `else` and
        // called `releaseOnLivePatch` — releasing the hide on the very message
        // that confirmed the close, after which a `/positions` response still in
        // flight put the closed row back on screen. Which hook won was render
        // order, so it failed intermittently.
        if (
          positionPatch &&
          (isClosedPositionAction(action) || isClosedPositionPatch(positionPatch))
        ) {
          // A close reported by the server is authoritative, unlike a local
          // submission, so record it with receipt-grade proof.
          registerPendingClose(positionPatch, "receipt");
        } else if (positionPatch) {
          releaseOnLivePatch(positionPatch);
        }
      }

      if (
        isOpenOrdersEndpoint &&
        channel === "orders" &&
        parsed?.data &&
        !Array.isArray(parsed.data)
      ) {
        applyOpenOrderEventToCache(
          parsed.data.order as Record<string, unknown> | undefined,
          parsed.data.action,
        );
      }

      shouldRevalidate = true;
    });

    if (shouldRevalidate) {
      revalidateOnRealtime();
    }
  }, [
    accountKey,
    enabled,
    lastMessages,
    realtimeChannels,
    revalidateOnRealtime,
    releaseOnLivePatch,
    isPositionsEndpoint,
    isOpenOrdersEndpoint,
    registerPendingClose,
    applyOpenOrdersSnapshotToCache,
    applyOpenOrderEventToCache,
  ]);
}
